import { ChangeDetectorRef, Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ResourceFilterParamsEnum } from 'src/app/models/params';
import { IFolderResponse, IResource, ResourceItemModel } from 'src/app/models/resource';
import { ResourcesService } from 'src/app/services/resources.service';
import { UserService } from 'src/app/services/user.service';
import { FiltersFactoryService } from 'src/app/services/filters-factory.service';
import { FiltersService } from 'src/app/services/filters.service';
import { OVERLAY_POSITIONS } from 'src/app/common/utils/overlay-position-constants';
import { FilesComponent } from '../files.component';

/**
 * To display navigation
 */
interface IDisplayPath {
  _id: string;
  name: string;
}

@UntilDestroy()
@Component({
  selector: 'app-files-extension[enforcingFilter][mandatoryType]',
  templateUrl: './files-extension.component.html',
  styleUrls: ['./files-extension.component.scss', '../files.component.scss'],
  providers: [FiltersService],
})
export class FilesExtensionComponent extends FilesComponent implements OnInit {
  OverlayPositions = OVERLAY_POSITIONS;

  path: IDisplayPath[] = [];

  // The enforced type of that dialog, it can be 'Image', 'Video', etc.
  @Input() mandatoryType!: ResourceItemModel | ResourceItemModel[];

  @Input() showResultsNumber = true;

  // To emit the selected file.
  @Output() fetchFile = new EventEmitter<IResource[]>();

  // auto upload file on double click
  @Output() autoUpload = new EventEmitter<boolean>();

  // When selected types exclude every mandatory type, send an impossible backend type filter
  // so the picker returns no files instead of widening back to the mandatory type set.
  private readonly noMatchingFileType = '__NO_MATCHING_FILE_TYPE__';

  gridView = true;

  constructor(
    _resourceService: ResourcesService,
    _router: Router,
    _dialog: MatDialog,
    _translate: TranslateService,
    _userService: UserService,
    _snackBar: MatSnackBar,
    _activatedRoute: ActivatedRoute,
    _changeDetection: ChangeDetectorRef,
    _filtersService: FiltersService,
    _filtersFactoryService: FiltersFactoryService,
  ) {
    super(
      _resourceService,
      _router,
      _dialog,
      _translate,
      _userService,
      _snackBar,
      _activatedRoute,
      _changeDetection,
      _filtersService,
      _filtersFactoryService,
    );
  }

  ngOnInit(): void {
    this.initializeFilesFilters();

    // init with root
    this.rootId = 'root';

    // Always display in GRID
    this.switchView(true);

    const requestedFolderId = this.rootId;
    this.getFolder(requestedFolderId, this.buildFilters()).then((folder) => {
      if (folder) {
        this.appendFilesAndFolders(folder, false, true);
        this.updateFolderContext(folder, requestedFolderId);
      }
    });
  }

  override buildFilters(): Record<string, any> {
    const param = super.buildFilters();

    const mandatoryTypes = this.getMandatoryTypes();
    if (mandatoryTypes.length) {
      const selectedTypes = this.getSelectedFileTypes(param.type);
      param.type = this.resolveTypeFilter(mandatoryTypes, selectedTypes);
      param[ResourceFilterParamsEnum.EnforcingFilter] = this.enforcingFilter;
    }
    return param;
  }

  openFolder(folder: IFolderResponse | string, append = false): void {
    // Clear all selections (files / folders).
    this.clearSelections();
    this.clearSearch();
    this.filePage = 1;
    this.folderPage = 1;
    // Keep active filters scoped to the selected folder while preserving the mandatory type constraint.
    this.getFolder(typeof folder === 'string' ? folder : folder._id, this.buildFilters()).then(
      (fetchedFolder) => {
        if (fetchedFolder) {
          this.appendFilesAndFolders(fetchedFolder, true, append);
        }
      },
    );
  }

  appendFilesAndFolders(folder: IFolderResponse, replace = false, append = false): void {
    // To edit the path view without reloading.
    if (this.activeFolder && append) {
      this.path.push({
        _id: this.activeFolder._id,
        name: this.activeFolder.name,
      });
    }
    super.appendFilesAndFolders(folder, replace);
  }

  backInPath(index: number): void {
    const folder = this.path[index];
    this.path.splice(index);
    this.openBreadcrumbFolder(folder);
  }

  /**
   * To reflect when selection.
   */
  handleToggleSelection(autoUpload = undefined): void {
    // close the panel whenever a file is selected
    this.filterPanelOpen = false;
    this.autoUpload.emit(autoUpload);
    this.fetchFile.emit(this.selectedFiles);
  }

  onScrollDown(): void {
    if (this.filesLoading$.getValue()) {
      return;
    }
    this.scrolling = true;
    this.getFolder(this.activeFolder?._id as string, this.buildFilters()).then((folder) => {
      if (folder) {
        this.appendFilesAndFolders(folder);
      }
      this.scrolling = false;
    });
  }

  /**
   * It removes All selection files and folders, which at most one selection at a time.
   */
  clearSelections(): void {
    this.toggleAll(false);

    // To inform the dialog parent.
    this.fetchFile.emit();
  }

  private getMandatoryTypes(): ResourceItemModel[] {
    const mandatoryTypes = Array.isArray(this.mandatoryType)
      ? this.mandatoryType
      : [this.mandatoryType];
    return mandatoryTypes.filter((type) => type !== this.resourceTypes.ANY);
  }

  private getSelectedFileTypes(typeFilter: unknown): string[] {
    if (!typeFilter) {
      return [];
    }
    return Array.isArray(typeFilter) ? typeFilter : [String(typeFilter)];
  }

  private resolveTypeFilter(
    mandatoryTypes: ResourceItemModel[],
    selectedTypes: string[],
  ): ResourceItemModel[] | string[] {
    if (!selectedTypes.length) {
      return mandatoryTypes;
    }

    const selectedAllowedTypes = selectedTypes.filter((type) =>
      mandatoryTypes.includes(type as ResourceItemModel),
    );
    if (selectedAllowedTypes.length) {
      return selectedAllowedTypes;
    }

    return [this.noMatchingFileType];
  }
}
